import React from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, MarkerClusterer } from '@react-google-maps/api';
import markerIcon from '../assets/images/marker.svg'
import ClusterImage from '../assets/images/cluster.png'
import {Link} from "../../plugins/gatsby-plugin-atollon";

const containerStyle = {
  width: '100%',
  height: '616px'
};

const greyStyle = [
  {
      "featureType": "all",
      "elementType": "all",
      "stylers": [
          {
              "hue": "#008eff"
          }
      ]
  },
  {
      "featureType": "poi",
      "elementType": "all",
      "stylers": [
          {
              "visibility": "off"
          }
      ]
  },
  {
      "featureType": "road",
      "elementType": "all",
      "stylers": [
          {
              "saturation": "0"
          },
          {
              "lightness": "0"
          }
      ]
  },
  {
      "featureType": "transit",
      "elementType": "all",
      "stylers": [
          {
              "visibility": "off"
          }
      ]
  },
  {
      "featureType": "water",
      "elementType": "all",
      "stylers": [
          {
              "visibility": "simplified"
          },
          {
              "saturation": "-60"
          },
          {
              "lightness": "-20"
          }
      ]
  }
];

const melbournePosition = { lat: -37.8136, lng: 144.9631 }

// Custom cluster styles
const clusterStyles = [
  {
    url: ClusterImage,
    height: 53,
    width: 53,
    textColor: '#ffffff',
    textSize: 14
  },
  {
    url: ClusterImage,
    height: 56,
    width: 56,
    textColor: '#ffffff',
    textSize: 14
  },
  {
    url: ClusterImage,
    height: 66,
    width: 66,
    textColor: '#ffffff',
    textSize: 14
  }
];

const DistributorMap = ({
  distributors,
  filteredDistributors,
  setFilteredDistributors,
  ausState,
  searchLocation,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyBT9h7XlGqHTkR0136mAxCfdsJXDZWpsqo',
  });

  const [map, setMap] = React.useState(null);
  const [markers, setMarkers] = React.useState([]);
  const [selectedDistributor, setSelectedDistributor] = React.useState(null);
  const [selectedPlace, setSelectedPlace] = React.useState(null);
  const [shouldAutoFitBounds, setShouldAutoFitBounds] = React.useState(true);

  React.useEffect(() => {
    if (!isLoaded || !map) return;

    setMarkers([]);

    const newMarkers = filteredDistributors
      .map((distributor, index) => {
        if (
          distributor.googleMap?.latitude &&
          distributor.googleMap?.longitude
        ) {
          return {
            position: {
              lat: distributor.googleMap.latitude,
              lng: distributor.googleMap.longitude,
            },
            icon: markerIcon,
            key: index,
            distributor: distributor,
          };
        } else {
          return null;
        }
      })
      .filter((marker) => marker !== null);

    setMarkers(newMarkers);

    if (shouldAutoFitBounds && newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach((marker) => bounds.extend(marker.position));
      map.fitBounds(bounds);
    }

    if (newMarkers.length === 1) map.setZoom(13);

    if (newMarkers.length === 0 && ausState !== '') {
      let states = [
        { label: 'All', value: '', latitude: -37.8136, longitude: 144.9631 },
        { label: 'ACT', value: 'act', latitude: -35.4735, longitude: 149.0124 },
        { label: 'NSW', value: 'nsw', latitude: -31.2532, longitude: 146.9211 },
        { label: 'NT', value: 'nt', latitude: -19.4914, longitude: 132.5509 },
        { label: 'QLD', value: 'qld', latitude: -20.9176, longitude: 142.7028 },
        { label: 'SA', value: 'sa', latitude: -30.0002, longitude: 136.2092 },
        { label: 'TAS', value: 'tas', latitude: -41.4545, longitude: 145.9707 },
        { label: 'VIC', value: 'vic', latitude: -36.5984, longitude: 144.678 },
        { label: 'WA', value: 'wa', latitude: -27.6728, longitude: 121.6283 },
      ];
      const stateCenter = states.find((s) => s.value === ausState?.value);
      if (stateCenter) {
        const center = {
          lat: stateCenter.latitude,
          lng: stateCenter.longitude,
        };
        map.setCenter(center);
        map.setZoom(6);
      }
      setShouldAutoFitBounds(true);
    }
  }, [isLoaded, map, filteredDistributors, ausState, shouldAutoFitBounds]);

  React.useEffect(() => {
    const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
      const toRad = (value) => (value * Math.PI) / 180;
      const R = 6371;

      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) *
          Math.cos(toRad(lat2)) *
          Math.sin(dLon / 2) ** 2;

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    const handleSearchSubmit = async () => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            searchLocation
          )}+AUS&key=AIzaSyBT9h7XlGqHTkR0136mAxCfdsJXDZWpsqo`
        );
        const data = await response.json();

        if (data.results.length > 0) {
          const location = data.results[0].geometry.location;
          setSelectedPlace({ lat: location.lat, lng: location.lng });

          const filtered = distributors.filter((distributor) => {
            const { latitude, longitude } = distributor.googleMap || {};
            if (latitude && longitude) {
              const distance = getDistanceInKm(
                location.lat,
                location.lng,
                latitude,
                longitude
              );
              return distance <= 20;
            }
            return false;
          });

          setFilteredDistributors(filtered);

          if (map) {
            setShouldAutoFitBounds(false);

            // Wait a tick to let markers render
            setTimeout(() => {
              map.setCenter(location);
              map.setZoom(13);
            }, 100); // You can tweak this — 100ms is usually enough
          }
        } else {
          console.error('No results found');
          setFilteredDistributors([]);
        }
      } catch (error) {
        console.error('Error fetching geocode data', error);
      }
    };

    if (searchLocation) {
      handleSearchSubmit();
    }
  }, [searchLocation]);

  const onLoad = (loadedMap) => {
    setMap(loadedMap);
  };

  const onUnmount = () => {
    setMap(null);
  };

  const handleMarkerClick = (distributor) => {
    setSelectedDistributor(distributor);
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={selectedPlace || melbournePosition}
      zoom={selectedPlace ? 14 : 10}
      options={{
        streetViewControl: false,
        fullscreenControl: false,
        styles: greyStyle,
      }}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      <MarkerClusterer gridSize={60} styles={clusterStyles}>
        {(clusterer) =>
          markers.map((marker) => (
            <Marker
              key={marker.key}
              position={marker.position}
              icon={marker.icon}
              onClick={() => handleMarkerClick(marker.distributor)}
              clusterer={clusterer}
            />
          ))
        }
      </MarkerClusterer>
      {selectedDistributor && (
        <InfoWindow
          position={{
            lat: selectedDistributor.googleMap?.latitude,
            lng: selectedDistributor.googleMap?.longitude,
          }}
          onCloseClick={() => setSelectedDistributor(null)}
        >
          <div className="card card--distributor">
            <div className="card--distributor__inner">
              {selectedDistributor.wholesaler && <h5>Wholesaler</h5>}
              {!selectedDistributor.wholesaler &&
                selectedDistributor.retailer && <h5>Retailer</h5>}
              {selectedDistributor?.title && (
                <h4>{selectedDistributor.title}</h4>
              )}
              {selectedDistributor?.address && (
                <p>{selectedDistributor.address}</p>
              )}
              {selectedDistributor?.postcode &&
                selectedDistributor.state && (
                  <p>
                    {selectedDistributor.state.toUpperCase()}{' '}
                    {selectedDistributor.postcode}
                  </p>
                )}

              <div className="card--distributor__contact">
                {selectedDistributor?.email && (
                  <p>
                    <span>E</span>
                    <Link to={`mailto:${selectedDistributor.email}`}>
                      {selectedDistributor.email}
                    </Link>
                  </p>
                )}
                {selectedDistributor?.phone && (
                  <p>
                    <span>P</span>
                    <Link to={`tel:${selectedDistributor.phone}`}>
                      {selectedDistributor.phone}
                    </Link>
                  </p>
                )}
                {selectedDistributor?.fax && (
                  <p>
                    <span>F</span>
                    <Link to={`fax:${selectedDistributor.fax}`}>
                      {selectedDistributor.fax}
                    </Link>
                  </p>
                )}
                {selectedDistributor?.website && (
                  <p>
                    <span>W</span>
                    <Link
                      to={selectedDistributor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedDistributor.website}
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default DistributorMap;